import React, { useState } from "react";
import { toastr } from "react-redux-toastr";
import { useAuth0 } from "@auth0/auth0-react";
import Skeleton from "@material-ui/lab/Skeleton";
import { Box, Typography, Switch } from "@material-ui/core";
// component
import MemberService from "Lib/API/Member/index";
// direct import
import { useStyles } from "./style";
import EventCard from "./EventsCard";
import { eventColors } from "./helper";

export default function Events({ selectedData, handleEvent }) {
  const classes = useStyles();
  const { getAccessTokenSilently } = useAuth0();
  // state
  const [loading, setLoading] = useState(false);
  const [archive, setArchive] = useState(false);
  const [listData, setEventCardData] = useState([]);
  const [expandedPast, setExpandedPast] = useState(null);
  const [expandedUpcoming, setExpandedUpcoming] = useState(null);

  const handleToggle = (type, index) => {
    if (type === "upcoming") {
      setExpandedUpcoming((prev) => (prev === index ? null : index));
    } else {
      setExpandedPast((prev) => (prev === index ? null : index));
    }
  };

  const fetchMyAPI = async () => {
    setLoading(true);
    const token = await getAccessTokenSilently();
    MemberService.getEventByContact({
      token,
      contactId: selectedData?.easy_contact_id,
    })
      .then((res) => {
        if (res) {
          setEventCardData(res?.data);
        }
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message || "Something went wrong");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  React.useEffect(() => {
    fetchMyAPI();
  }, []);

  const loaderSkeleton = (
    <Skeleton variant="rect" height={280} style={{ borderRadius: 6 }} />
  );

  const pastLength = listData?.past?.length ?? 0;
  const todayLength = listData?.today?.length ?? 0;
  const archiveLength = listData?.archived?.length ?? 0;
  const upcomingLength = listData?.upcoming?.length ?? 0;

  return (
    <Box className={classes.scrollContainer} style={{ overflow: "hidden" }}>
      {/* Upcoming Events */}
      <Box display="flex" alignItems="center" sx={{ my: 2 }}>
        <Typography className={classes.sectionTitle}>
          {archive ? "Archived" : "Upcoming"} Events
        </Typography>

        <span className={classes.badge}>
          {archive ? archiveLength ?? 0 : upcomingLength ?? 0}
        </span>
        <Box className={classes.archivedBtn}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="10"
            viewBox="0 0 11 10"
            fill="none"
          >
            <path
              d="M10.7975 0.8775L9.8175 0.15C9.705 0.0675 9.5025 0 9.3625 0H1.6375C1.4975 0 1.295 0.0675 1.1825 0.15L0.2025 0.8775C0.09 0.9625 0 1.14 0 1.28V2.5575C0 2.6975 0.1125 2.81 0.2525 2.81H10.7475C10.8875 2.81 11 2.6975 11 2.5575V1.28C11 1.14 10.91 0.9625 10.7975 0.8775ZM0.4075 8.6625C0.4075 8.8875 0.59 9.07 0.815 9.07H10.185C10.41 9.07 10.5925 8.8875 10.5925 8.6625V3.3675H0.4075V8.6625ZM4.0975 4.4175H6.9025C7.1925 4.4175 7.4275 4.655 7.4275 4.945C7.4275 5.235 7.1925 5.47 6.9025 5.47H4.0975C3.8075 5.47 3.5725 5.2325 3.5725 4.945C3.57 4.6525 3.8075 4.4175 4.0975 4.4175Z"
              fill="#002E5C"
            />
          </svg>
          <Box sx={{ ml: 1, opacity: 0.8 }}>Archived Events</Box>
          <Switch
            checked={archive}
            onChange={() => setArchive(!archive)}
            name="checkedA"
            inputProps={{ "aria-label": "primary checkbox" }}
          />
        </Box>
      </Box>

      {loading && loaderSkeleton}
      {!archive && (
        <Box className={classes.eventCardStyle}>
          {listData?.upcoming?.map((event, idx) => (
            <EventCard
              key={`upcoming-${idx}`}
              event={event}
              idx={idx}
              color={eventColors[idx % eventColors.length]}
              isExpanded={expandedUpcoming === idx}
              onToggle={handleToggle}
              type="upcoming"
              classes={classes}
              handleEvent={handleEvent}
            />
          ))}
        </Box>
      )}

      {/* Archived Events */}

      {archive && (
        <Box className={classes.eventCardStyle}>
          {listData?.archived?.map((event, idx) => (
            <EventCard
              key={`archived-${idx}`}
              event={event}
              idx={idx}
              color={eventColors[idx % eventColors.length]}
              isExpanded={expandedUpcoming === idx}
              onToggle={handleToggle}
              type="archived"
              classes={classes}
              handleEvent={handleEvent}
            />
          ))}
        </Box>
      )}

      {/* Today Events */}
      {!archive && todayLength !== 0 && (
        <Box display="flex" alignItems="center" sx={{ my: 2 }}>
          <Typography className={classes.sectionTitle}>
            Todays Events
          </Typography>
          <span className={classes.badge}>{todayLength ?? 0}</span>
        </Box>
      )}

      {!archive && loading && loaderSkeleton}
      {!archive && todayLength !== 0 && (
        <Box className={classes.eventCardStyle}>
          {listData?.today?.map((event, idx) => (
            <EventCard
              key={`today-${idx}`}
              event={event}
              idx={idx}
              color={eventColors[idx % eventColors.length]}
              isExpanded={expandedPast === idx}
              onToggle={handleToggle}
              type="today"
              classes={classes}
              handleEvent={handleEvent}
            />
          ))}
        </Box>
      )}

      {/* Past Events */}
      {!archive && pastLength !== 0 && (
        <Box display="flex" alignItems="center" sx={{ my: 2 }}>
          <Typography className={classes.sectionTitle}>Past Events</Typography>
          <span className={classes.badge}>{pastLength}</span>
        </Box>
      )}

      {!archive && loading && loaderSkeleton}
      {!archive && pastLength !== 0 && (
        <Box className={classes.eventCardStyle}>
          {listData?.past?.map((event, idx) => (
            <EventCard
              key={`past-${idx}`}
              event={event}
              idx={idx}
              color={eventColors[idx % eventColors.length]}
              isExpanded={expandedPast === idx}
              onToggle={handleToggle}
              type="past"
              classes={classes}
              handleEvent={handleEvent}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
